import { type RefObject, useEffect } from "react";

/**
 * Calls `loadMore` when the scroll box under `ref` is scrolled to its end.
 *
 * Deliberately scroll-driven rather than visibility-driven: a list shorter
 * than its box must not keep fetching on its own — the next page comes when
 * the person reaches the last row.
 */
export function useLoadMore(
  ref: RefObject<HTMLElement | null>,
  hasMore: boolean,
  loading: boolean,
  loadMore: () => void,
): void {
  useEffect(() => {
    const element = ref.current;
    if (!element || !hasMore) return;
    const onScroll = () => {
      const remaining = element.scrollHeight - element.scrollTop - element.clientHeight;
      if (remaining <= 8 && !loading) loadMore();
    };
    element.addEventListener("scroll", onScroll, { passive: true });
    return () => element.removeEventListener("scroll", onScroll);
  }, [ref, hasMore, loading, loadMore]);
}
